import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FileText, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { invoiceSettingsAPI, cspireAPI, salesTaxRatesAPI } from '@shared/apiClient';

export default function InvoiceSettingsPanel() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [siteTaxDrafts, setSiteTaxDrafts] = useState({});
  const [stateTaxDrafts, setStateTaxDrafts] = useState({});

  const { data: settingsData, isLoading: settingsLoading } = useQuery(
    'invoiceSettings',
    () => invoiceSettingsAPI.getSettings()
  );

  const { data: sitesData, isLoading: sitesLoading } = useQuery(
    ['cspireSites', 'invoice-settings'],
    () => cspireAPI.getSites({ limit: 500, page: 1 })
  );

  const { data: taxRatesData, isLoading: taxRatesLoading } = useQuery(
    'salesTaxRates',
    () => salesTaxRatesAPI.getRates()
  );

  const { data: clientStatesData, isLoading: clientStatesLoading } = useQuery(
    'salesTaxClientStates',
    () => salesTaxRatesAPI.getClientStates()
  );

  const settings = settingsData?.data?.settings;
  const sites = sitesData?.data?.sites || [];
  const savedTaxRates = taxRatesData?.data?.rates || [];
  const clientStates = clientStatesData?.data?.states || [];

  useEffect(() => {
    if (!settings) return;
    setForm({
      companyName: settings.companyName || '',
      addressLine1: settings.addressLine1 || '',
      addressLine2: settings.addressLine2 || '',
      addressLine3: settings.addressLine3 || '',
      phone: settings.phone || '',
      email: settings.email || '',
      logoPath: settings.logoPath || '',
      defaultTaxRate: settings.defaultTaxRate != null ? Number(settings.defaultTaxRate) : 0.07,
      footerText: settings.footerText || '',
      franklinSendToEmail: settings.franklinSendToEmail || ''
    });
  }, [settings]);

  const updateSettingsMutation = useMutation(
    (data) => invoiceSettingsAPI.updateSettings(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('invoiceSettings');
        toast.success('Invoice settings saved');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to save invoice settings');
      }
    }
  );

  const updateSiteMutation = useMutation(
    ({ id, data }) => cspireAPI.updateSite(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['cspireSites', 'invoice-settings']);
        toast.success('Site tax settings updated');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update site tax settings');
      }
    }
  );

  const updateStateTaxMutation = useMutation(
    (rates) => salesTaxRatesAPI.updateRates(rates),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('salesTaxRates');
        setStateTaxDrafts({});
        toast.success('State sales tax settings saved');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to save state sales tax settings');
      }
    }
  );

  const stateRows = [...new Set([
    ...clientStates,
    ...savedTaxRates.map((rate) => rate.state)
  ])].sort();

  const getStateDraft = (state) => {
    if (stateTaxDrafts[state]) return stateTaxDrafts[state];
    const saved = savedTaxRates.find((rate) => rate.state === state);
    return {
      rate: saved?.rate != null ? Number(saved.rate) : (form?.defaultTaxRate ?? 0.07),
      laborTaxable: saved?.laborTaxable !== false,
      partsTaxable: saved?.partsTaxable !== false,
      otherTaxable: saved?.otherTaxable !== false,
      enabled: saved?.enabled !== false
    };
  };

  const updateStateDraft = (state, field, value) => {
    setStateTaxDrafts((prev) => ({
      ...prev,
      [state]: {
        ...getStateDraft(state),
        [field]: value
      }
    }));
  };

  const saveStateTax = (state) => {
    const draft = getStateDraft(state);
    updateStateTaxMutation.mutate([{
      state,
      rate: Number(draft.rate),
      laborTaxable: draft.laborTaxable,
      partsTaxable: draft.partsTaxable,
      otherTaxable: draft.otherTaxable,
      enabled: draft.enabled
    }]);
  };

  const saveAllStateTax = () => {
    const rates = stateRows.map((state) => {
      const draft = getStateDraft(state);
      return {
        state,
        rate: Number(draft.rate),
        laborTaxable: draft.laborTaxable,
        partsTaxable: draft.partsTaxable,
        otherTaxable: draft.otherTaxable,
        enabled: draft.enabled
      };
    });
    updateStateTaxMutation.mutate(rates);
  };

  const getSiteDraft = (site) => {
    if (siteTaxDrafts[site.id]) return siteTaxDrafts[site.id];
    return {
      defaultTaxRate: site.defaultTaxRate != null ? Number(site.defaultTaxRate) : '',
      taxExempt: !!site.taxExempt
    };
  };

  const updateSiteDraft = (siteId, field, value) => {
    setSiteTaxDrafts((prev) => ({
      ...prev,
      [siteId]: {
        ...getSiteDraft({ id: siteId, ...sites.find((s) => s.id === siteId) }),
        [field]: value
      }
    }));
  };

  const saveSiteTax = (site) => {
    const draft = getSiteDraft(site);
    updateSiteMutation.mutate({
      id: site.id,
      data: {
        defaultTaxRate: draft.defaultTaxRate === '' ? null : Number(draft.defaultTaxRate),
        taxExempt: draft.taxExempt
      }
    });
  };

  if (settingsLoading || !form) {
    return (
      <div className="bg-white shadow rounded-lg p-6 text-sm text-gray-600">
        Loading invoice settings...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <FileText className="h-5 w-5 text-gray-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Company & Invoice Defaults</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          These values appear on C Spire invoice PDFs and emails. Per-site tax defaults can override the fallback rate below.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company name</label>
            <input
              type="text"
              value={form.companyName}
              onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              className="block w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fallback default tax rate (%)</label>
            <input
              type="number"
              step="0.1"
              value={(form.defaultTaxRate * 100).toFixed(1)}
              onChange={(e) => setForm({ ...form, defaultTaxRate: parseFloat(e.target.value) / 100 || 0 })}
              className="block w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address line 1</label>
            <input type="text" value={form.addressLine1} onChange={(e) => setForm({ ...form, addressLine1: e.target.value })} className="block w-full rounded border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address line 2</label>
            <input type="text" value={form.addressLine2} onChange={(e) => setForm({ ...form, addressLine2: e.target.value })} className="block w-full rounded border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address line 3</label>
            <input type="text" value={form.addressLine3} onChange={(e) => setForm({ ...form, addressLine3: e.target.value })} className="block w-full rounded border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="block w-full rounded border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="block w-full rounded border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL/path</label>
            <input type="text" value={form.logoPath} onChange={(e) => setForm({ ...form, logoPath: e.target.value })} className="block w-full rounded border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Footer text</label>
            <input type="text" value={form.footerText} onChange={(e) => setForm({ ...form, footerText: e.target.value })} className="block w-full rounded border border-gray-300 px-3 py-2 text-sm" placeholder="Leave blank to use phone + email" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Franklin send-to email</label>
            <input
              type="email"
              value={form.franklinSendToEmail}
              onChange={(e) => setForm({ ...form, franklinSendToEmail: e.target.value })}
              className="block w-full rounded border border-gray-300 px-3 py-2 text-sm"
              placeholder="billing@example.com"
            />
            <p className="mt-1 text-xs text-gray-500">
              Destination for the Franklin mailbox's Send action in Invoicing (completed-call PDF + summary).
            </p>
          </div>
        </div>

        <div className="mt-4">
          <button
            type="button"
            onClick={() => updateSettingsMutation.mutate(form)}
            disabled={updateSettingsMutation.isLoading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-1.5" />
            Save company settings
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6" data-tour-id="invoice-settings-state-tax">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Sales tax by state</h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure sales tax rates and category rules per state based on customer addresses. Labor, parts, and other line items can be taxed differently (for example, labor may be non-taxable in Alabama). Customers marked tax exempt are never taxed.
        </p>

        {taxRatesLoading || clientStatesLoading ? (
          <p className="text-sm text-gray-500">Loading state tax settings...</p>
        ) : stateRows.length === 0 ? (
          <p className="text-sm text-gray-500">No customer states found yet. Add customers with a state to configure rules here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-600">
                  <th className="py-2 pr-4">State</th>
                  <th className="py-2 pr-4">Rate (%)</th>
                  <th className="py-2 pr-4">Labor taxable</th>
                  <th className="py-2 pr-4">Parts taxable</th>
                  <th className="py-2 pr-4">Other taxable</th>
                  <th className="py-2 pr-4">Enabled</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {stateRows.map((state) => {
                  const draft = getStateDraft(state);
                  return (
                    <tr key={state} className="border-b border-gray-100">
                      <td className="py-2 pr-4 font-medium">{state}</td>
                      <td className="py-2 pr-4">
                        <input
                          type="number"
                          step="0.1"
                          value={(Number(draft.rate) * 100).toFixed(1)}
                          onChange={(e) => updateStateDraft(state, 'rate', parseFloat(e.target.value) / 100 || 0)}
                          className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="py-2 pr-4">
                        <input
                          type="checkbox"
                          checked={draft.laborTaxable}
                          onChange={(e) => updateStateDraft(state, 'laborTaxable', e.target.checked)}
                        />
                      </td>
                      <td className="py-2 pr-4">
                        <input
                          type="checkbox"
                          checked={draft.partsTaxable}
                          onChange={(e) => updateStateDraft(state, 'partsTaxable', e.target.checked)}
                        />
                      </td>
                      <td className="py-2 pr-4">
                        <input
                          type="checkbox"
                          checked={draft.otherTaxable}
                          onChange={(e) => updateStateDraft(state, 'otherTaxable', e.target.checked)}
                        />
                      </td>
                      <td className="py-2 pr-4">
                        <input
                          type="checkbox"
                          checked={draft.enabled}
                          onChange={(e) => updateStateDraft(state, 'enabled', e.target.checked)}
                        />
                      </td>
                      <td className="py-2">
                        <button
                          type="button"
                          onClick={() => saveStateTax(state)}
                          disabled={updateStateTaxMutation.isLoading}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          Save
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="mt-4">
              <button
                type="button"
                onClick={saveAllStateTax}
                disabled={updateStateTaxMutation.isLoading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-1.5" />
                Save all state tax settings
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">C Spire site tax defaults</h3>
        <p className="text-sm text-gray-600 mb-4">
          New invoices for each site pre-fill tax on/off and rate from these settings. Admins can still override on each invoice.
        </p>

        {sitesLoading ? (
          <p className="text-sm text-gray-500">Loading sites...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-600">
                  <th className="py-2 pr-4">Site</th>
                  <th className="py-2 pr-4">Location</th>
                  <th className="py-2 pr-4">Default tax (%)</th>
                  <th className="py-2 pr-4">Tax exempt</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {sites.map((site) => {
                  const draft = getSiteDraft(site);
                  return (
                    <tr key={site.id} className="border-b border-gray-100">
                      <td className="py-2 pr-4 font-medium">{site.siteId}</td>
                      <td className="py-2 pr-4">{site.locationName || '—'}</td>
                      <td className="py-2 pr-4">
                        <input
                          type="number"
                          step="0.1"
                          value={draft.defaultTaxRate === '' ? '' : (Number(draft.defaultTaxRate) * 100).toFixed(1)}
                          onChange={(e) => updateSiteDraft(
                            site.id,
                            'defaultTaxRate',
                            e.target.value === '' ? '' : parseFloat(e.target.value) / 100
                          )}
                          className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
                          placeholder="7.0"
                        />
                      </td>
                      <td className="py-2 pr-4">
                        <input
                          type="checkbox"
                          checked={draft.taxExempt}
                          onChange={(e) => updateSiteDraft(site.id, 'taxExempt', e.target.checked)}
                        />
                      </td>
                      <td className="py-2">
                        <button
                          type="button"
                          onClick={() => saveSiteTax(site)}
                          disabled={updateSiteMutation.isLoading}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          Save
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
